import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    MinigameSession,
    MinigameStats,
    CreateMinigameSessionRequest,
    UpdateMinigameSessionRequest,
    VocabularyWord,
    PaginationParams,
} from '@/services/vocabularyService';

// === MINIGAME SESSIONS ACTIONS ===
export const createMinigameSessionRequest = createAction<CreateMinigameSessionRequest>(
    'minigame/createMinigameSessionRequest',
);
export const createMinigameSessionSuccess = createAction<MinigameSession>('minigame/createMinigameSessionSuccess');
export const createMinigameSessionFailure = createAction<string>('minigame/createMinigameSessionFailure');

export const updateMinigameSessionRequest = createAction<UpdateMinigameSessionRequest>(
    'minigame/updateMinigameSessionRequest',
);
export const updateMinigameSessionSuccess = createAction<MinigameSession>('minigame/updateMinigameSessionSuccess');
export const updateMinigameSessionFailure = createAction<string>('minigame/updateMinigameSessionFailure');

export const fetchMinigameSessionsRequest = createAction<{
    gameType?: MinigameSession['gameType'];
    pagination?: PaginationParams;
}>('minigame/fetchMinigameSessionsRequest');
export const fetchMinigameSessionsSuccess = createAction<{
    results: MinigameSession[];
    count: number;
    next?: string;
    previous?: string;
}>('minigame/fetchMinigameSessionsSuccess');
export const fetchMinigameSessionsFailure = createAction<string>('minigame/fetchMinigameSessionsFailure');

export const fetchMinigameStatsRequest = createAction<MinigameSession['gameType'] | undefined>(
    'minigame/fetchMinigameStatsRequest',
);
export const fetchMinigameStatsSuccess = createAction<MinigameStats[]>('minigame/fetchMinigameStatsSuccess');
export const fetchMinigameStatsFailure = createAction<string>('minigame/fetchMinigameStatsFailure');

export const generateMinigameWordsRequest = createAction<{
    gameType: MinigameSession['gameType'];
    difficulty: 'easy' | 'medium' | 'hard';
    count?: number;
    groupId?: string;
}>('minigame/generateMinigameWordsRequest');
export const generateMinigameWordsSuccess = createAction<VocabularyWord[]>('minigame/generateMinigameWordsSuccess');
export const generateMinigameWordsFailure = createAction<string>('minigame/generateMinigameWordsFailure');

// === STATE INTERFACE ===
export interface MinigameState {
    minigameSessions: MinigameSession[];
    currentMinigameSession: MinigameSession | null;
    minigameStats: MinigameStats[];
    generatedMinigameWords: VocabularyWord[];

    // Loading States
    isLoadingMinigameSessions: boolean;
    isCreatingMinigameSession: boolean;
    isUpdatingMinigameSession: boolean;
    isLoadingMinigameStats: boolean;
    isGeneratingMinigameWords: boolean;

    // Error States
    minigameError: string | null;
}

const initialState: MinigameState = {
    minigameSessions: [],
    currentMinigameSession: null,
    minigameStats: [],
    generatedMinigameWords: [],
    isLoadingMinigameSessions: false,
    isCreatingMinigameSession: false,
    isUpdatingMinigameSession: false,
    isLoadingMinigameStats: false,
    isGeneratingMinigameWords: false,
    minigameError: null,
};

const minigameSlice = createSlice({
    name: 'minigame',
    initialState,
    reducers: {
        setCurrentMinigameSession: (state, action: PayloadAction<MinigameSession | null>) => {
            state.currentMinigameSession = action.payload;
        },
        clearMinigameError: (state) => {
            state.minigameError = null;
        },
        clearGeneratedWords: (state) => {
            state.generatedMinigameWords = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === CREATE MINIGAME SESSION ===
            .addCase(createMinigameSessionRequest, (state) => {
                state.isCreatingMinigameSession = true;
                state.minigameError = null;
            })
            .addCase(createMinigameSessionSuccess, (state, action) => {
                state.isCreatingMinigameSession = false;
                state.minigameSessions.unshift(action.payload);
                state.currentMinigameSession = action.payload;
            })
            .addCase(createMinigameSessionFailure, (state, action) => {
                state.isCreatingMinigameSession = false;
                state.minigameError = action.payload;
            })

            // === UPDATE MINIGAME SESSION ===
            .addCase(updateMinigameSessionRequest, (state) => {
                state.isUpdatingMinigameSession = true;
                state.minigameError = null;
            })
            .addCase(updateMinigameSessionSuccess, (state, action) => {
                state.isUpdatingMinigameSession = false;
                const index = state.minigameSessions.findIndex((session) => session.id === action.payload.id);
                if (index !== -1) {
                    state.minigameSessions[index] = action.payload;
                }
                if (state.currentMinigameSession?.id === action.payload.id) {
                    state.currentMinigameSession = action.payload;
                }
            })
            .addCase(updateMinigameSessionFailure, (state, action) => {
                state.isUpdatingMinigameSession = false;
                state.minigameError = action.payload;
            })

            // === FETCH MINIGAME SESSIONS ===
            .addCase(fetchMinigameSessionsRequest, (state) => {
                state.isLoadingMinigameSessions = true;
                state.minigameError = null;
            })
            .addCase(fetchMinigameSessionsSuccess, (state, action) => {
                state.isLoadingMinigameSessions = false;
                state.minigameSessions = action.payload.results;
            })
            .addCase(fetchMinigameSessionsFailure, (state, action) => {
                state.isLoadingMinigameSessions = false;
                state.minigameError = action.payload;
            })

            // === FETCH MINIGAME STATS ===
            .addCase(fetchMinigameStatsRequest, (state) => {
                state.isLoadingMinigameStats = true;
                state.minigameError = null;
            })
            .addCase(fetchMinigameStatsSuccess, (state, action) => {
                state.isLoadingMinigameStats = false;
                state.minigameStats = action.payload;
            })
            .addCase(fetchMinigameStatsFailure, (state, action) => {
                state.isLoadingMinigameStats = false;
                state.minigameError = action.payload;
            })

            // === GENERATE MINIGAME WORDS ===
            .addCase(generateMinigameWordsRequest, (state) => {
                state.isGeneratingMinigameWords = true;
                state.minigameError = null;
            })
            .addCase(generateMinigameWordsSuccess, (state, action) => {
                state.isGeneratingMinigameWords = false;
                state.generatedMinigameWords = action.payload;
            })
            .addCase(generateMinigameWordsFailure, (state, action) => {
                state.isGeneratingMinigameWords = false;
                state.minigameError = action.payload;
            });
    },
});

export const { setCurrentMinigameSession, clearMinigameError, clearGeneratedWords } = minigameSlice.actions;

export default minigameSlice.reducer;
